import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Repeat, Power, PowerOff, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'AED', 'SGD'];
const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly', 'custom'];

const formatMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount || 0);

const Recurring = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRule, setPreviewRule] = useState(null);
  const [previewRuns, setPreviewRuns] = useState([]);
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: '',
    currency: 'USD',
    category: '',
    project: '',
    department: '',
    description: '',
    frequency: 'monthly',
    custom_interval_days: '',
    last_day_of_month: false,
    weekdays: [],
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
  });
  const canEdit = user?.role !== 'viewer';

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/recurring`);
      setRules(res.data);
    } catch {
      toast.error('Failed to load recurring rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        type: formData.type,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        category: formData.category,
        project: formData.project || null,
        department: formData.department || null,
        description: formData.description,
        frequency: formData.frequency,
        custom_interval_days: formData.frequency === 'custom' ? parseInt(formData.custom_interval_days) : null,
        last_day_of_month: formData.frequency === 'monthly' ? !!formData.last_day_of_month : false,
        weekdays: formData.frequency === 'weekly' && formData.weekdays?.length ? formData.weekdays : null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };
      if (editingId) {
        // Backend doesn't allow changing frequency/start_date — send only mutable fields
        const updatePayload = {
          amount: payload.amount,
          currency: payload.currency,
          category: payload.category,
          project: payload.project,
          department: payload.department,
          description: payload.description,
          end_date: payload.end_date,
        };
        await axios.put(`${API}/recurring/${editingId}`, updatePayload);
        toast.success('Recurring rule updated');
      } else {
        await axios.post(`${API}/recurring`, payload);
        toast.success('Recurring rule created');
      }
      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recurring rule? Already-generated transactions are kept.')) return;
    try {
      await axios.delete(`${API}/recurring/${id}`);
      toast.success('Recurring rule deleted');
      fetchRules();
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(`${API}/recurring/${rule.id}`, { active: !rule.active });
      toast.success(rule.active ? 'Paused' : 'Resumed');
      fetchRules();
    } catch {
      toast.error('Failed to update rule');
    }
  };

  const handleEdit = (r) => {
    setEditingId(r.id);
    setFormData({
      type: r.type,
      amount: String(r.amount),
      currency: r.currency || 'USD',
      category: r.category,
      project: r.project || '',
      department: r.department || '',
      description: r.description,
      frequency: r.frequency,
      custom_interval_days: r.custom_interval_days ? String(r.custom_interval_days) : '',
      last_day_of_month: !!r.last_day_of_month,
      weekdays: Array.isArray(r.weekdays) ? r.weekdays : [],
      start_date: new Date(r.start_date).toISOString().split('T')[0],
      end_date: r.end_date ? new Date(r.end_date).toISOString().split('T')[0] : '',
    });
    setDialogOpen(true);
  };

  const handlePreview = async (r) => {
    try {
      const res = await axios.get(`${API}/recurring/${r.id}/preview?count=5`);
      setPreviewRule(r);
      setPreviewRuns(res.data.next_runs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      type: 'expense',
      amount: '',
      currency: 'USD',
      category: '',
      project: '',
      department: '',
      description: '',
      frequency: 'monthly',
      custom_interval_days: '',
      last_day_of_month: false,
      weekdays: [],
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
    });
  };

  return (
    <div className="p-8 md:p-12" data-testid="recurring-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="recurring-title">
              Recurring
            </h1>
            <p className="text-slate-600 mt-1 font-ibm">
              Automate income & expenses that repeat — daily, weekly, monthly, or yearly
            </p>
          </div>
          {canEdit && (
            <Dialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="add-recurring-button"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                >
                  <Plus size={18} className="mr-2" />
                  Add Recurring
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-chivo text-2xl">
                    {editingId ? 'Edit Recurring Rule' : 'New Recurring Rule'}
                  </DialogTitle>
                  <DialogDescription className="text-slate-600 font-ibm text-sm">
                    {editingId
                      ? 'Update this recurring rule. Frequency and start date are fixed once created.'
                      : 'Define a transaction that should be created automatically on a schedule.'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="recurring-form">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Type</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value) => setFormData({ ...formData, type: value })}
                        disabled={!!editingId}
                      >
                        <SelectTrigger data-testid="recurring-input-type" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="income">Income</SelectItem>
                          <SelectItem value="expense">Expense</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Amount</Label>
                      <Input
                        data-testid="recurring-input-amount"
                        type="number"
                        step="0.01"
                        min="0.01"
                        required
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Currency</Label>
                      <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                        <SelectTrigger data-testid="recurring-input-currency" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Frequency</Label>
                      <Select
                        value={formData.frequency}
                        onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                        disabled={!!editingId}
                      >
                        <SelectTrigger data-testid="recurring-input-frequency" className="mt-2 bg-slate-50 border-slate-200 rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map((f) => (
                            <SelectItem key={f} value={f}>
                              {f.charAt(0).toUpperCase() + f.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Category</Label>
                      <Input
                        data-testid="recurring-input-category"
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      />
                    </div>
                  </div>
                  {formData.frequency === 'weekly' && (
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                        Days of week (optional — defaults to start date weekday)
                      </Label>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="recurring-weekdays">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, i) => {
                          const checked = formData.weekdays?.includes(i);
                          return (
                            <label
                              key={label}
                              className={`px-3 py-2 border rounded-sm cursor-pointer font-ibm text-xs ${
                                editingId ? 'opacity-50 pointer-events-none ' : ''
                              }${
                                checked
                                  ? 'border-slate-900 bg-slate-900 text-white'
                                  : 'border-slate-300 text-slate-700 hover:border-slate-500'
                              }`}
                            >
                              <input
                                type="checkbox"
                                disabled={!!editingId}
                                checked={!!checked}
                                onChange={() => {
                                  const cur = new Set(formData.weekdays || []);
                                  if (cur.has(i)) cur.delete(i); else cur.add(i);
                                  setFormData({ ...formData, weekdays: [...cur].sort((a,b)=>a-b) });
                                }}
                                data-testid={`recurring-weekday-${i}`}
                                className="hidden"
                              />
                              {label}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs font-ibm text-slate-500 mt-1">
                        Pick one or more weekdays for the schedule (e.g. Mon/Wed/Fri).
                      </p>
                    </div>
                  )}
                  {formData.frequency === 'custom' && (
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                        Every (days)
                      </Label>
                      <Input
                        data-testid="recurring-input-custom-days"
                        type="number"
                        min="1"
                        max="365"
                        required
                        disabled={!!editingId}
                        value={formData.custom_interval_days}
                        onChange={(e) => setFormData({ ...formData, custom_interval_days: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                        placeholder="e.g., 14 for every 2 weeks"
                      />
                    </div>
                  )}
                  {formData.frequency === 'monthly' && (
                    <div className="flex items-center gap-2 pl-1">
                      <input
                        id="lastDayOfMonth"
                        data-testid="recurring-last-day"
                        type="checkbox"
                        disabled={!!editingId}
                        checked={!!formData.last_day_of_month}
                        onChange={(e) => setFormData({ ...formData, last_day_of_month: e.target.checked })}
                        className="w-4 h-4 accent-slate-900"
                      />
                      <Label htmlFor="lastDayOfMonth" className="text-sm font-ibm text-slate-700 cursor-pointer">
                        Run on the <strong>last day</strong> of every month
                      </Label>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Start Date</Label>
                      <Input
                        data-testid="recurring-input-start-date"
                        type="date"
                        required
                        disabled={!!editingId}
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">
                        End Date (Optional)
                      </Label>
                      <Input
                        data-testid="recurring-input-end-date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Description</Label>
                    <Textarea
                      data-testid="recurring-input-description"
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-2 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-slate-900 rounded-sm"
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      data-testid="submit-recurring"
                      className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
                    >
                      {editingId ? 'Update' : 'Create'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-sm hover:scale-[0.98] transition-transform"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-slate-900 font-ibm" data-testid="recurring-loading">
            Loading...
          </div>
        ) : rules.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="no-recurring">
            <Repeat className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-xl font-chivo font-bold text-slate-900 mb-2">No recurring rules yet</h3>
            <p className="text-slate-600 font-ibm text-sm">
              {canEdit
                ? 'Set up monthly rent, weekly subscriptions, or yearly renewals — they will be created automatically.'
                : 'No recurring rules have been configured yet.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="recurring-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Description</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Frequency</th>
                    <th className="px-6 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Next Run</th>
                    {canEdit && <th className="px-6 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`recurring-row-${idx}`}>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm ${
                            r.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}
                          data-testid={`recurring-status-${idx}`}
                        >
                          {r.active ? 'Active' : 'Paused'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block px-3 py-1 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm ${
                            r.type === 'income' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                          }`}
                        >
                          {r.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-ibm text-sm text-slate-900">
                        <div className="font-medium">{r.description}</div>
                        <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{r.category}</div>
                      </td>
                      <td className="px-6 py-4 font-ibm text-sm text-slate-900 capitalize">{r.frequency}</td>
                      <td
                        className={`px-6 py-4 text-right font-ibm font-semibold tabular-nums ${
                          r.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                        }`}
                      >
                        {r.type === 'income' ? '+' : '-'}{formatMoney(r.amount, r.currency)}
                      </td>
                      <td className="px-6 py-4 font-ibm text-sm text-slate-600">
                        {r.active ? new Date(r.next_run_date).toLocaleDateString() : '—'}
                      </td>
                      {canEdit && (
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => handlePreview(r)}
                              data-testid={`recurring-preview-${idx}`}
                              className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                              title="Preview next 5 runs"
                            >
                              <Eye size={16} className="text-slate-600" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(r)}
                              data-testid={`recurring-toggle-${idx}`}
                              className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                              title={r.active ? 'Pause' : 'Resume'}
                            >
                              {r.active ? (
                                <PowerOff size={16} className="text-amber-600" />
                              ) : (
                                <Power size={16} className="text-emerald-600" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEdit(r)}
                              data-testid={`recurring-edit-${idx}`}
                              className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                              title="Edit"
                            >
                              <Edit size={16} className="text-slate-600" />
                            </button>
                            <button
                              onClick={() => handleDelete(r.id)}
                              data-testid={`recurring-delete-${idx}`}
                              className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-red-600" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>

      <Dialog open={!!previewRule} onOpenChange={(open) => { if (!open) { setPreviewRule(null); setPreviewRuns([]); } }}>
        <DialogContent className="bg-white rounded-sm max-w-md" data-testid="recurring-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo text-2xl">Next 5 Runs</DialogTitle>
            <DialogDescription className="text-slate-600 font-ibm text-sm">
              {previewRule?.description} · {previewRule?.frequency}
              {previewRule?.last_day_of_month && ' (last day of month)'}
              {previewRule?.custom_interval_days && ` every ${previewRule.custom_interval_days} days`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {previewRuns.length === 0 ? (
              <p className="text-sm text-slate-500 font-ibm" data-testid="preview-empty">No more runs (rule may have ended).</p>
            ) : (
              previewRuns.map((d, i) => (
                <div
                  key={d}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-100"
                  data-testid={`preview-run-${i}`}
                >
                  <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">
                    Run #{i + 1}
                  </span>
                  <span className="font-ibm font-medium text-slate-900 tabular-nums">
                    {new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Recurring;
